import {
  matchServedDomain,
  normaliseHost,
  normaliseIdentityUrlsByDomain,
  resolveIdentityUrl,
} from './identityDomain';

const Config = {
  BASE_URL: 'https://api.s.insights.welbee.co.uk/api',
  ADMIN_BASE_URL: 'https://api.s.admin.welbee.co.uk/api',
  App_Url: 'https://s.insights.welbee.co.uk',
  PPL_MNGMT_URL: 'https://s.home.welbee.co.uk',
  ADMIN_API_URL: 'https://api.s.admin.welbee.co.uk',
  HOME_URL: 'https://s.home.welbee.co.uk',
  PPL_MNGMT_BASE_URL: 'https://api.s.peoplemanagement.welbee.co.uk/api',
  EI_URL: 'https://ei.welbee.co.uk',
  IDENTITY_URL: 'https://d.identity.welbee.co.uk',
  /**
   * Identity app per registrable domain, for domains other than the one IDENTITY_URL names.
   * A domain absent here falls back to IDENTITY_URL, so nothing changes on welbee.co.uk.
   */
  /**
   * Sibling-app URLs per registrable domain. The kiosk exit, the QR warning and the check-in
   * dashboard link all send the user to another product; a value naming one domain hands them to
   * welbee.co.uk from the EI host, where the session cookie cannot follow.
   * A key absent here falls back to the value above.
   */
  APP_URLS_BY_DOMAIN: {
    'edu-intelligence.ai': {
      HOME_URL: 'https://s.home.edu-intelligence.ai',
      PPL_MNGMT_URL: 'https://s.home.edu-intelligence.ai',
      EI_URL: 'https://s.app.edu-intelligence.ai',
    },
  },
  IDENTITY_URLS_BY_DOMAIN: {
    'edu-intelligence.ai': 'https://s.identity.edu-intelligence.ai',
  },
}

/**
 * The Identity app URL for the domain this browser is actually on.
 *
 * Prefer this over `Config.IDENTITY_URL` anywhere the value is used to reach the Identity app.
 * `IDENTITY_URL` names a single registrable domain, and the session cookie does not cross
 * registrable domains — so on the other domain a credentialed call to it is sent without the
 * cookie and comes back 401, which reads as "signed out immediately after signing in".
 */
function getIdentityUrl() {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;

  return resolveIdentityUrl(
    host,
    normaliseIdentityUrlsByDomain(Config.IDENTITY_URLS_BY_DOMAIN),
    Config.IDENTITY_URL
  );
}

/**
 * Sibling-app URLs for the domain this browser is actually on. Resolution is per key, so a domain
 * configuring only some apps keeps the value from Config for the rest.
 */
function getAppUrls(
  host = typeof window === 'undefined' ? '' : window.location.hostname
) {
  const byDomain = Config.APP_URLS_BY_DOMAIN || {};
  // matchServedDomain returns the normalised form, so a key written with a leading dot would
  // match and then miss the lookup.
  const normalised = Object.fromEntries(
    Object.entries(byDomain).map(([k, v]) => [normaliseHost(k).replace(/^\./, ''), v])
  );
  const matched = matchServedDomain(host, Object.keys(normalised));
  const overrides = (matched && normalised[matched]) || {};

  return {
    HOME_URL: overrides.HOME_URL || Config.HOME_URL,
    PPL_MNGMT_URL: overrides.PPL_MNGMT_URL || Config.PPL_MNGMT_URL,
    EI_URL: overrides.EI_URL || Config.EI_URL,
  };
}

export {Config, getIdentityUrl, getAppUrls}
